import { useEffect } from "react";

interface HistoryKeys {
  /** Off while the builder is not on screen (another tab) or cannot be edited. */
  enabled: boolean;
  onUndo: () => void;
  onRedo: () => void;
}

const TEXT_FIELDS = new Set(["INPUT", "TEXTAREA", "SELECT"]);
/** Open overlays (the step sheet, the Add-step menu, a confirmation): text and focus belong to them. */
const OVERLAY = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]';
/** An element the author types into without being a field (rich text); `isContentEditable` alone is not set in every environment. */
const EDITABLE = '[contenteditable=""], [contenteditable="true"]';
/** An overlay that is open right now, wherever focus is (Radix marks the open ones). */
const OPEN_OVERLAY = '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"][data-state="open"]';

/** Where the browser's own undo (of typed text) wins: a field, an editable element, or anything inside an overlay. */
export function ownsUndo(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return TEXT_FIELDS.has(target.tagName) || target.isContentEditable || target.closest(`${EDITABLE}, ${OVERLAY}`) !== null;
}

/**
 * Ctrl / Cmd + Z undoes, Ctrl / Cmd + Shift + Z and Ctrl + Y redo (WF-P5-14). Typing in a field keeps its own undo, and
 * nothing happens while an overlay is open: its content is not what Undo would change.
 */
export function useHistoryKeys({ enabled, onUndo, onRedo }: HistoryKeys): void {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || !(event.ctrlKey || event.metaKey)) return;
      const key = event.key.toLowerCase();
      const redo = (key === "z" && event.shiftKey) || (key === "y" && event.ctrlKey && !event.shiftKey);
      const undo = key === "z" && !event.shiftKey;
      if (!redo && !undo) return;
      if (ownsUndo(event.target) || document.querySelector(OPEN_OVERLAY)) return;
      event.preventDefault();
      if (redo) onRedo();
      else onUndo();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => { document.removeEventListener("keydown", onKeyDown); };
  }, [enabled, onUndo, onRedo]);
}
